'use client'

import { usePathname } from "next/navigation"
import { useEffect, useMemo, useRef, useState } from "react"
import { formatImageURL, formatPrice, getBackgroundColorStyles, getBorderColorStyles, getTextColorStyles, getVendorPrimaryColor, getEffectivePrice, getStrikethroughPrice, getDisplayDiscounts, isDiscountBetter, isCustomerAuthActive, getProductTags, getYoutubeVideos, getYoutubeVideoId, getVariantDimensions, getSuggestedProducts, formatFileSize, getBuyXGetYTagText, getBuyXGetYFallbackTitle, getLoyaltyProductDiscount } from "@/utils/util"
import { trackViewContent } from "@/utils/tracking"
import { ArrowRight, BadgePercent, ChevronDown, ChevronLeft, ChevronRight, CircleX, Crown, Download, FileDown, FileText, Gift, Hammer, Heart, LoaderCircle, Medal, Minus, PackageX, Percent, Plus, ShoppingCart, Star, Truck, Wallet } from "lucide-react"
import { useCart, useWishlist, useVariantSelection, useProductGallery, useProductQuantity, useCartActions } from "@/lib/commerce/hooks"
import { useScrollVisibility } from "@/hooks/use-scroll-visibility"
import { useSession } from "next-auth/react"
import { navigateUrl } from "@/utils/client-request"
import { sendRequest } from "@/utils/request"
import { Button } from "@/components/elements/Button"
import Input from "@/components/elements/Input"
import ProductDropdown from "@/components/blocks/ProductDropdown"
import Review from "./Review"
import ImageCarousel from "@/components/blocks/ImageCarousel"
import ProductDescription from "@/components/blocks/ProductDescription"
import TermsDrawer from "@/components/blocks/TermsDrawer"
import ProductCard from "./ProductCard"
import VariantOptionPicker from "@/components/blocks/VariantOptionPicker"
import { hasOptions } from "@/lib/commerce/variantOptions"

import Image from "next/image"
import Storepay from "@/assets/storepay.png"
import Pocket from "@/assets/pocket.png"
import Lend from "@/assets/lendmn.png"
import Omniway from "@/assets/omniway.png"
import Qpay from "@/assets/qpay.svg"
import TransferIcon from "@/assets/transfer.png"
import StorepayDrawer from "@/components/blocks/StorepayDrawer"
import PocketDrawer from "@/components/blocks/PocketDrawer"
import LendDrawer from "@/components/blocks/LendDrawer"

export default function ProductDetail({ productsList, product, vendor, reviews }: { productsList: any[], product: any, vendor: any, reviews: any, wishlist?: any }) {
  const pathname = usePathname()
  const { data: session } = useSession()
  const { loading, loyaltySummary, loyaltyCategoryParents } = useCart()
  const { isInWishlist: isInWishlistFn, toggle: toggleWishlistHook, loading: wishlistLoading } = useWishlist()
  const { addToCartWithRedirect, buyNow, buyNowLoading } = useCartActions(vendor)
  const [isCarouselOpen, setIsCarouselOpen] = useState(false)
  const [termsDrawerOpen, setTermsDrawerOpen] = useState(false)
  const [storepayDrawerOpen, setStorepayDrawerOpen] = useState(false)
  const [pocketDrawerOpen, setPocketDrawerOpen] = useState(false)
  const [lendDrawerOpen, setLendDrawerOpen] = useState(false)
  const [selectedTermsType, setSelectedTermsType] = useState<'delivery' | 'payment' | 'service' | null>(null)
  const { selectedVariantId, setSelectedVariantId, selectedVariant, isVariantAvailable, selectedOptions, selectOption, isSelectionPurchasable } = useVariantSelection(product)

  useEffect(() => {
    if (product?.id) trackViewContent(product)
  }, [product?.id])

  const discounts = getDisplayDiscounts(product, !!session?.user?.id, selectedVariantId ?? undefined)
  const effectivePrice = getEffectivePrice(selectedVariant, discounts)
  const strikethroughPrice = getStrikethroughPrice(selectedVariant, discounts)
  const loyaltyDeal = getLoyaltyProductDiscount({ product, variant: selectedVariant, summary: loyaltySummary, categoryParents: loyaltyCategoryParents, hasOtherDiscount: discounts?.length > 0 && isDiscountBetter(selectedVariant, discounts) })
  const displayEffective = loyaltyDeal ? Math.max(0, (effectivePrice ?? selectedVariant?.price ?? 0) - loyaltyDeal.unitSaving) : effectivePrice
  const displayStrike = loyaltyDeal ? (selectedVariant?.price ?? 0) : strikethroughPrice
  const canPurchase = isSelectionPurchasable && (!product?.isTrackable || (selectedVariant?.stock ?? 0) > 0)

  const images = selectedVariant?.images || []
  const imageUrl = images.length > 0 ? formatImageURL(images[0]) : formatImageURL('placeholder.png')
  const { activeImage, activeImageIndex, swipe: handleImageSwipe, selectIndex: handleIndicatorClick, handleTouchStart, handleTouchMove, handleTouchEnd } = useProductGallery(images)
  const [canReview, setCanReview] = useState(false)
  const { quantity, setQuantity, handleChange: handleQuantityChange, handleBlur } = useProductQuantity({ variant: selectedVariant, initial: 1 })
  const imageRef = useRef<HTMLDivElement>(null)
  const isNavVisible = useScrollVisibility()
  const [activeGalleryTab, setActiveGalleryTab] = useState<'images' | 'videos'>('images')

  const TagLinks = ({ tags }: { tags: string[] }) => {
    return (
      <div className="flex flex-wrap gap-2">
        {tags.map((tag, index) => (
          <a key={index} href={navigateUrl(`/product?tag=${encodeURIComponent(tag)}`, pathname)} className={`${textColorClass} hover:opacity-80 text-sm font-medium bg-muted px-2.5 py-0.5 rounded-md transition-colors`} style={textColorStyle}>
            {tag}
          </a>
        ))}
      </div>
    )
  }

  const averageRating = useMemo(() => {
    return reviews?.average || 0
  }, [reviews])

  const publishedReviewsCount = reviews?.totalCount || 0

  const suggestedProducts = useMemo(() => getSuggestedProducts(product, productsList || [], 5), [product, productsList])

  const isInWishlist = isInWishlistFn(product.id, selectedVariantId ?? undefined)

  useEffect(() => {
    setCanReview(!!session?.user?.id)
  }, [session])

  const toggleWishlist = (e: any) => {
    if (e) e.preventDefault()
    toggleWishlistHook({ productId: product.id, variantId: selectedVariant?.id })
  }

  const handleAddToCart = async (e: any) => {
    e.preventDefault()
    await addToCartWithRedirect({ productId: product.id, quantity: typeof quantity === 'number' ? quantity : 1, variantId: selectedVariant.id })
  }

  const handleBuyNow = async (e: any) => {
    e.preventDefault()
    await buyNow({ productId: product.id, variantId: selectedVariant.id, quantity: typeof quantity === 'number' ? quantity : 1 })
  }

  const handleTermsClick = (type: 'delivery' | 'payment' | 'service') => {
    setSelectedTermsType(type)
    setTermsDrawerOpen(true)
  }

  const handleStorepayClick = () => {
    setStorepayDrawerOpen(true)
  }

  const handlePocketClick = () => {
    setPocketDrawerOpen(true)
  }

  const handleLendClick = () => {
    setLendDrawerOpen(true)
  }


  const primaryColor = getVendorPrimaryColor(vendor)

  const { className: backgroundColorClass, style: backgroundColorStyle } = getBackgroundColorStyles(primaryColor)
  const { className: textColorClass, style: textColorStyle } = getTextColorStyles(primaryColor)
  const { className: borderColorClass, style: borderColorStyle } = getBorderColorStyles(primaryColor)

  return (
    <div className="cont py-8">
      <ImageCarousel images={images} initialImage={activeImage ?? ''} isOpen={isCarouselOpen} onClose={() => setIsCarouselOpen(false)} productName={product.name} vendor={vendor} />
      <TermsDrawer isOpen={termsDrawerOpen} onClose={() => setTermsDrawerOpen(false)} type={selectedTermsType} terms={vendor?.terms || {}} vendor={vendor} />
      <StorepayDrawer isOpen={storepayDrawerOpen} onClose={() => setStorepayDrawerOpen(false)} vendor={vendor} productPrice={effectivePrice ?? selectedVariant?.price ?? 0} productName={product.name} productImage={activeImage ? activeImage : imageUrl} />
      <PocketDrawer isOpen={pocketDrawerOpen} onClose={() => setPocketDrawerOpen(false)} vendor={vendor} productPrice={effectivePrice ?? selectedVariant?.price ?? 0} productName={product.name} productImage={activeImage ? activeImage : imageUrl} />
      <LendDrawer isOpen={lendDrawerOpen} onClose={() => setLendDrawerOpen(false)} vendor={vendor} productPrice={effectivePrice ?? selectedVariant?.price ?? 0} productName={product.name} productImage={activeImage ? activeImage : imageUrl} />

      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground mb-4 md:mb-8">
        <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
        <ChevronRight className="h-3 w-3 opacity-50" />
        <a href={navigateUrl('/product', pathname)} className="hover:text-foreground">Бүтээгдэхүүн</a>
        {product?.categories?.[0] && (
          <>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <a href={navigateUrl(`/product?category=${product.categories[0].id}`, pathname)} className="hover:text-foreground">{product.categories[0].name}</a>
          </>
        )}
        {product?.brand && (
          <>
            <ChevronRight className="h-3 w-3 opacity-50" />
            <a href={navigateUrl(`/product?brand=${product.brand.id}`, pathname)} className="hover:text-foreground">{product.brand.name}</a>
          </>
        )}
        <ChevronRight className="h-3 w-3 opacity-50" />
        <span className="text-foreground font-medium">{product.name}</span>
      </div>

      {/* Right rail spans both rows so the description can sit flush under the gallery.
          grid-rows-[auto_1fr] pins row 1 to the gallery's height — without it the browser
          splits the taller rail's surplus across both rows and opens a gap under the image. */}
      <div className="lg:grid lg:grid-cols-12 lg:gap-x-6 lg:gap-y-8 lg:grid-rows-[auto_1fr] space-y-4 lg:space-y-0">
        <div className="lg:col-span-8 lg:col-start-1 lg:row-start-1">
          <div className={`md:max-w-none md:max-h-none md:w-full ${activeGalleryTab === 'images' ? 'block' : 'hidden'}`}>
            <div className="block sm:hidden relative">
              <div ref={imageRef} className="w-full aspect-square relative overflow-hidden rounded-lg cursor-pointer" onClick={() => { setIsCarouselOpen(true); handleIndicatorClick(activeImageIndex) }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
                <img src={activeImage ? formatImageURL(activeImage) : imageUrl} alt={product.name} className="w-full h-full object-contain transition-transform duration-300" />
                {images.length > 1 && (
                  <div className="absolute top-4 right-4 bg-black/50 text-white px-2 py-1 rounded-full text-xs">
                    {activeImageIndex + 1}/{images.length}
                  </div>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex justify-center mt-4 gap-1.5">
                  {images.map((_: any, index: number) => (
                    <button key={index} onClick={() => handleIndicatorClick(index)} className={`transition-all duration-200 rounded-full ${index === activeImageIndex ? `w-6 h-2 ${backgroundColorClass}` : 'w-2 h-2 bg-muted-foreground/30 hover:bg-muted-foreground/50'}`} aria-label={`View image ${index + 1}`} style={index === activeImageIndex ? backgroundColorStyle : {}} />
                  ))}
                </div>
              )}
            </div>
            <div className="hidden sm:block">
              <div className="flex gap-6">
                {images && images?.length > 0 && (
                  <div className="flex flex-col gap-3 shrink-0">
                    {images?.slice(0, 5).map((image: any, index: number) => (
                      <div
                        key={index}
                        className={`relative w-24 h-24 sm:w-28 sm:h-28 shrink-0 cursor-pointer rounded-lg border-2 ${activeImage === image ? `${borderColorClass}` : "border-transparent"}`}
                        onClick={() => {
                          if (index === 4 && images.length > 5) {
                            handleIndicatorClick(4)
                            setIsCarouselOpen(true)
                          } else {
                            handleIndicatorClick(index)
                          }
                        }}
                        style={activeImage === image ? borderColorStyle : {}}
                      >
                        <img src={formatImageURL(image)} alt={product.name} className="absolute inset-0 w-full h-full object-cover rounded-lg p-0.5" />
                        {index === 4 && images.length > 5 && (
                          <div className="absolute inset-0 bg-black/20 rounded-lg flex items-center justify-center">
                            <span className="text-white font-semibold text-lg">
                              +{images.length - 5}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex-1 min-w-0 relative">
                  <div className="aspect-w-1 aspect-h-1 w-full relative">
                    <img src={activeImage ? formatImageURL(activeImage) : imageUrl} alt={product.name} className="w-full h-full object-contain rounded-lg cursor-pointer" onClick={() => setIsCarouselOpen(true)} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {getYoutubeVideos(product).length > 0 && (
            <div className={`grid grid-cols-1 gap-4 ${activeGalleryTab === 'videos' ? 'block' : 'hidden'}`}>
              {getYoutubeVideos(product).map((url: string, index: number) => {
                const videoId = getYoutubeVideoId(url)
                if (!videoId) return null
                return (
                  <div key={index} className="relative w-full rounded-lg overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                    <iframe src={`https://www.youtube.com/embed/${videoId}`} title={`${product.name} video ${index + 1}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="absolute top-0 left-0 w-full h-full border-0" />
                  </div>
                )
              })}
            </div>
          )}
          {getYoutubeVideos(product).length > 0 && (
            <div className="grid grid-cols-2 gap-2 mt-4">
              <Button tag="button" border={vendor?.border} variant={activeGalleryTab === 'images' ? 'secondary' : 'ghost'} onClick={() => setActiveGalleryTab('images')}>
                Зураг
              </Button>
              <Button tag="button" border={vendor?.border} variant={activeGalleryTab === 'videos' ? 'secondary' : 'ghost'} onClick={() => setActiveGalleryTab('videos')}>
                Видео
              </Button>
            </div>
          )}
        </div>
        <div className="lg:col-span-4 lg:col-start-9 lg:row-start-1 lg:row-span-2">
          {product.brand && (
            <div className="mb-2">
              <a href={navigateUrl(`/product?brand=${product.brand.id}`, pathname)} className={`font-normal text-muted-foreground leading-6 hover:underline`}>
                {product.brand.name}
              </a>
            </div>
          )}
          <div className="flex items-center justify-between">
            <div className="text-xl font-medium leading-7 tracking-[.0125em] text-foreground">{product?.name}</div>
            {isCustomerAuthActive(vendor) && (
              <div onClick={(e) => toggleWishlist(e)} className={`bg-foreground/20 transition-all p-2 rounded-full cursor-pointer shrink-0 hover:bg-foreground/30`}>
                {wishlistLoading ? (
                  <LoaderCircle className={`h-5 w-5 text-white animate-spin`} />
                ) : (
                  <Heart className={`h-5 w-5 ${isInWishlist ? `text-white fill-white` : `text-white`}`} />
                )}
              </div>
            )}
          </div>
          {vendor?.canReview && (
            <div className="mt-1">
              <div className="flex items-center gap-1">
                <Star aria-hidden="true" className="text-yellow-500 fill-yellow-500 shrink-0 size-4" />
                <p className="text-sm leading-5 tracking-[.0125em] font-medium">{averageRating || 0}</p>
                {publishedReviewsCount > 0 && (
                  <span className="text-sm text-muted-foreground leading-5">({publishedReviewsCount})</span>
                )}
              </div>
            </div>
          )}
          <div className="mt-5">
            {selectedVariant?.price && selectedVariant?.price > 0 && (
              displayEffective !== null ? (
                displayEffective === 0 ? (
                  <div className="flex gap-2.5 items-center font-medium">
                    <span className="text-lg sm:text-xl text-green-600 font-medium leading-7 tracking-[.025em]">Үнэгүй</span>
                    <span className="text-base sm:text-lg line-through text-muted-foreground leading-5 tracking-[.0125em]">{formatPrice(displayStrike)}</span>
                  </div>
                ) : (
                  <div className="flex gap-2.5 items-center font-medium">
                    <span className="text-lg sm:text-xl text-red-600 font-medium leading-7 tracking-[.025em]">{formatPrice(displayEffective)}</span>
                    <span className="text-base sm:text-lg line-through text-muted-foreground leading-5 tracking-[.0125em]">{formatPrice(displayStrike)}</span>
                    {selectedVariant?.priceSalePercent && !isDiscountBetter(selectedVariant, discounts) && !loyaltyDeal?.saleOffset && (
                      <div className="bg-orange-600 text-white px-1.5 py-1 rounded-full font-semibold leading-4 text-xs flex items-center gap-0.5">
                        <BadgePercent className="h-4 w-4 fill-orange-600 bg-orange-600" />
                        <span>-{selectedVariant.priceSalePercent}%</span>
                      </div>
                    )}
                    {loyaltyDeal && (
                      <div className="bg-violet-600 text-white px-1.5 py-1 rounded-full font-semibold tabular-nums leading-4 text-xs flex items-center gap-0.5">
                        <Medal className="h-4 w-4" />
                        <span>-{loyaltyDeal.percent}%</span>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <span className="text-lg sm:text-xl leading-7 tracking-[.0125em] font-medium">{formatPrice(selectedVariant?.price)}</span>
              )
            )}
            {loyaltyDeal && (
              <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-md bg-violet-50 border border-violet-200 text-violet-700 px-3 py-1.5 text-xs font-medium">
                <Medal className="size-4 shrink-0" />
                <span>{loyaltyDeal.tierName} түвшний хөнгөлөлт {loyaltyDeal.percent}%-д хамрагдах бараа тул <span className="font-semibold tabular-nums">{formatPrice(loyaltyDeal.finalPrice)}</span> хөнгөлөлт захиалгад автоматаар тооцогдоно</span>
              </div>
            )}
          </div>
          <DiscountLedger
            discounts={[
              ...(isDiscountBetter(selectedVariant, discounts) ? discounts.filter((d: any) => d.type === 'productDiscount') : []),
              ...discounts.filter((d: any) => d.type === 'buyXgetY'),
              ...discounts.filter((d: any) => d.type === 'freeShipping'),
            ]}
            pathname={pathname}
          />
          <form className="mt-6">
            <div>
              {hasOptions(product) ? (
                <VariantOptionPicker product={product} selected={selectedOptions} onSelect={selectOption} selectedClassName={`${backgroundColorClass} text-white border-transparent`} selectedStyle={{ ...backgroundColorStyle, color: 'white' }} />
              ) : product?.variants.length > 1 ? (
                <>
                  <div className="text-foreground leading-6 font-medium">Бүтээгдэхүүний төрлүүд</div>
                  <div className="mt-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2 gap-2">
                      {product?.variants?.map((variant: any, index: number) => {
                        const isAvailable = isVariantAvailable(variant)
                        const isSelected = selectedVariantId === variant.id
                        const vDiscounts = getDisplayDiscounts(product, !!session?.user?.id, variant.id)
                        const hasPriceSale = variant.priceSale && variant.priceSale > 0 && variant.priceSale < variant.price
                        const hasDiscountSale = vDiscounts?.some((d: any) => d.type === 'productDiscount' && d.method !== 'code') || vDiscounts?.some((d: any) => d.type === 'buyXgetY')
                        return (
                          <div key={index} className="flex items-center relative">
                            <input type="radio" name="variant" id={`variant-${index}`} value={variant.id} checked={isSelected} onChange={() => setSelectedVariantId(variant.id)} className="hidden peer" />
                            <label htmlFor={`variant-${index}`} className={`flex items-center justify-center min-h-[44px] h-full px-3 py-2 text-sm font-medium rounded-md transition-colors w-full text-center relative cursor-pointer ${!isAvailable ? isSelected ? 'bg-red-100 text-red-700 border-2 border-red-300' : 'bg-muted hover:bg-muted-foreground/20 text-muted-foreground' : isSelected ? `${backgroundColorClass} text-white border-transparent` : 'text-foreground bg-muted hover:bg-muted-foreground/20 transition-colors'}`} style={isAvailable ? isSelected ? { ...backgroundColorStyle, color: 'white' } : { '--hover-text-color': textColorStyle?.color } as React.CSSProperties : {}}>
                              <span className={`${!isAvailable ? 'line-through' : ''}`}>
                                {variant.name}
                              </span>
                              {(hasPriceSale || hasDiscountSale) && (
                                <div className="absolute -top-1.5 -right-1.5 px-1.5 py-1 bg-orange-600 text-white rounded-full">
                                  {hasDiscountSale ? <Gift className="w-4 h-4" strokeWidth={2.5} /> : <Percent className="w-4 h-4" strokeWidth={2.5} />}
                                </div>
                              )}
                            </label>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              ) : (
                product?.variant?.name && product.variant.name !== 'Үндсэн төрөл' && (
                  <div className="text-foreground leading-6 font-medium">{product?.variant?.name}</div>
                )
              )}
            </div>
            <div className="flex mt-6">
              {vendor?.canPlaceOrder !== false && (
                <div className={`md:hidden fixed left-0 z-20 w-full px-4 md:px-0 md:relative transition-all duration-300 ${isNavVisible ? 'bottom-20' : 'bottom-4'}`}>
                  {canPurchase ? (
                    <Button tag="button" border={vendor?.border} color={vendor?.color?.primary} icon={ShoppingCart} width="full" onClick={handleAddToCart} disabled={loading}>
                      {loading ? 'Нэмж байна…' : 'Сагсанд хийх'}
                    </Button>
                  ) : (
                    <></>
                  )}
                </div>
              )}
              {canPurchase ? (
                <div className="flex flex-col gap-y-1.5 w-full">
                  <div className="flex items-center justify-between">
                    <span className="text-foreground leading-6 font-medium">Тоо хэмжээ</span>
                    <div className="w-28 shrink-0">
                      <Input
                        type="number"
                        value={quantity === "" ? "" : quantity}
                        onChange={(e) => handleQuantityChange(e.target.value)}
                        onBlur={handleBlur}
                        placeholder="0"
                        className="w-full"
                        inputClassName="text-center bg-muted border-none"
                        leftIcon={Minus}
                        rightIcon={Plus}
                        leftAction={() => {
                          if (quantity === "" || quantity <= 1) {
                            setQuantity(1)
                          } else {
                            setQuantity(quantity - 1)
                          }
                        }}
                        rightAction={() => {
                          if (quantity !== "" && typeof quantity === "number") {
                            if (product?.isTrackable && selectedVariant?.stock) {
                              if (quantity < selectedVariant.stock) {
                                setQuantity(quantity + 1)
                              }
                            } else {
                              setQuantity(quantity + 1)
                            }
                          } else if (quantity === "") {
                            setQuantity(1)
                          }
                        }}
                      />
                    </div>
                  </div>
                  {vendor?.showStock && (
                    product?.isTrackable && (
                      selectedVariant?.stock && selectedVariant?.stock > 0 ? (
                        <div className="text-sm text-muted-foreground font-normal leading-5 flex justify-end">
                          Үлдэгдэл {selectedVariant?.stock}
                        </div>
                      ) : (
                        <div className="rounded-md bg-red-50 p-4">
                          <div className="flex items-center gap-3">
                            <div className="shrink-0">
                              <CircleX className="h-5 w-5 text-red-500" aria-hidden="true" />
                            </div>
                            <div className="text-foreground text-sm font-medium leading-5">Энэ төрлийн бараа дууссан байна.</div>
                          </div>
                        </div>
                      )
                    )
                  )}
                  {vendor?.canPlaceOrder !== false && (
                    <div className="flex flex-col gap-y-2 mt-4">
                      <Button tag="button" border={vendor?.border} color={vendor?.color?.primary} width="full" icon={ShoppingCart} onClick={handleAddToCart} disabled={loading} variant="secondary">
                        <div className="flex items-center justify-center gap-2">
                          <span>{loading ? 'Нэмж байна…' : 'Сагсанд хийх'}</span>
                          <div className="w-1 h-1 bg-muted-foreground/60 rounded-full"></div>
                          {!loading && (
                            <span className="font-medium text-sm text-foreground/90 leading-6">
                              {formatPrice((displayEffective ?? selectedVariant?.price) * (quantity || 1))}
                            </span>
                          )}
                        </div>
                      </Button>
                      <Button tag="button" border={vendor?.border} color={vendor?.color?.primary} width="full" icon={Wallet} onClick={handleBuyNow} disabled={loading || buyNowLoading}>
                        {buyNowLoading ? 'Уншиж байна' : 'Худалдаж авах'}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <Button border={vendor?.border} color={vendor?.color?.primary} icon={PackageX} width="full" disabled>
                  Дууссан байна
                </Button>
              )}
            </div>
          </form>
          {vendor?.canPlaceOrder == false && (
            <div className="mt-6">
              <div className="bg-muted border-l-4 border-muted-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <PackageX className="w-5 h-5 text-foreground" />
                  <h3 className="font-semibold text-foreground text-sm">
                    Захиалга түр хаагдсан байна
                  </h3>
                </div>
                <p className="text-xs text-muted-foreground ml-7 leading-5">
                  Манай дэлгүүр нь түр хугацаанд захиалга хүлээн авах боломжгүй болсон тул та дараа дахин оролдоно уу.
                </p>
              </div>
            </div>
          )}
          <FulfillmentBanner product={product} variant={selectedVariant} />
          <div className="mt-6">
            <ProductDropdown
              details={[
                ...(getVariantDimensions(selectedVariant) ? [{ name: "Хэмжээс", desc: getVariantDimensions(selectedVariant) }] : []),
                ...((getProductTags(product) || []).length > 0 ? [{ name: "Шошго", desc: <TagLinks tags={getProductTags(product) || []} /> }] : []),
              ]}
            />
          </div>
          {(() => {
            const isAllowed = (key: string) => product?.paymentOptions?.[key] !== false
            const showQpay = vendor?.qpayMerchant && vendor?.canQpay !== false && isAllowed('qpay')
            const showTransfer = vendor?.canTransfer && vendor?.canSimple !== false && isAllowed('transfer')
            const showStorepay = vendor?.storepayCredentials && vendor?.canStorepay !== false && isAllowed('storepay')
            const showPocket = vendor?.pocketCredentials && vendor?.canPocket !== false && isAllowed('pocket')
            const showLend = vendor?.lendCredentials && vendor?.canLend !== false && isAllowed('lend')
            const showOmniway = vendor?.omniwayCredentials && vendor?.canOmniway !== false && isAllowed('omniway')
            if (!showQpay && !showTransfer && !showStorepay && !showPocket && !showLend && !showOmniway) return null
            return (
              <div className="mt-6">
                <div className="flex flex-col gap-1 mb-4">
                  <div className="text-foreground leading-6 font-medium">Төлбөрийн боломжууд</div>
                  <span className="text-xs text-muted-foreground">Та энэхүү бүтээгдэхүүнийг дараах төлбөрийн хэлбэрээр худалдан авах боломжтой.</span>
                </div>
                <div className="space-y-3">
                  {showQpay && (
                    <div className="flex items-center gap-5 font-medium border border-border rounded-md px-4 py-3 shadow-xs">
                      <Image src={Qpay} alt="QPay" className="h-8 w-8 object-contain" />
                      <div className="flex flex-col gap-0.5 w-full">
                        <span className="text-base text-foreground/90 leading-5 tracking-[.0125em]">QPay</span>
                        <span className="mt-1 text-xs text-muted-foreground font-normal">Бүх банкны аппаар QR код уншуулан төлөх</span>
                      </div>
                    </div>
                  )}

                  {showTransfer && (
                    <div className="flex items-center gap-5 font-medium border border-border rounded-md px-4 py-3 shadow-xs">
                      <Image src={TransferIcon} alt="Transfer" className="h-8 w-8 object-contain" />
                      <div className="flex flex-col gap-0.5 w-full">
                        <span className="text-base text-foreground/90 leading-5 tracking-[.0125em]">Дансаар шилжүүлэх</span>
                        <span className="mt-1 text-xs text-muted-foreground font-normal">Дэлгүүрийн дансанд шилжүүлэг хийж төлөх</span>
                      </div>
                    </div>
                  )}

                  {showStorepay && (
                    <div onClick={handleStorepayClick} className="flex items-center gap-5 font-medium border border-border rounded-md px-4 py-3 shadow-xs cursor-pointer hover:border-blue-500 transition-colors">
                      <Image src={Storepay} alt="StorePay" className="h-8 w-8 object-cover" />
                      <div className="flex flex-col gap-0.5 w-full">
                        <div className="flex items-center justify-between">
                          <span className="text-base text-foreground/90 leading-5 tracking-[.0125em]">StorePay</span>
                          <span className="text-xs text-muted-foreground font-medium italic whitespace-nowrap"><span className="hidden lg:inline xl:hidden">(100,000₮+)</span><span className="inline lg:hidden xl:inline">(Зөвхөн 100,000₮-с дээш)</span></span>
                        </div>
                        <span className="mt-1 text-xs text-muted-foreground font-normal">Үнийн дүнг 4-6 хуваан төлөх боломжтой</span>
                      </div>
                    </div>
                  )}

                  {showPocket && (
                    <div onClick={handlePocketClick} className="flex items-center gap-5 font-medium border border-border rounded-md px-4 py-3 shadow-xs cursor-pointer hover:border-red-500 transition-colors">
                      <Image src={Pocket} alt="Pocket" className="h-8 w-8 object-cover" />
                      <div className="flex flex-col gap-0.5 w-full">
                        <div className="flex items-center justify-between">
                          <span className="text-base text-foreground/90 leading-5 tracking-[.0125em]">PocketZero</span>
                          <span className="text-xs text-muted-foreground font-medium italic whitespace-nowrap"><span className="hidden lg:inline xl:hidden">(100,000₮+)</span><span className="inline lg:hidden xl:inline">(Зөвхөн 100,000₮-с дээш)</span></span>
                        </div>
                        <span className="mt-1 text-xs text-muted-foreground font-normal">Урьдчилгаагүй, шимтгэлгүй 30-90 хоногт 2-6 хувааж төлөх боломжтой</span>
                      </div>
                    </div>
                  )}

                  {showLend && (
                    <div onClick={handleLendClick} className="flex items-center gap-5 font-medium border border-border rounded-md px-4 py-3 shadow-xs cursor-pointer hover:border-orange-500 transition-colors">
                      <Image src={Lend} alt="Lend" className="h-8 w-8 object-contain" />
                      <div className="flex flex-col gap-0.5 w-full">
                        <div className="flex items-center justify-between">
                          <span className="text-base text-foreground/90 leading-5 tracking-[.0125em]">LendMN</span>
                          <span className="text-xs text-muted-foreground font-medium italic whitespace-nowrap"><span className="hidden lg:inline xl:hidden">(100,000₮+)</span><span className="inline lg:hidden xl:inline">(Зөвхөн 100,000₮-с дээш)</span></span>
                        </div>
                        <span className="mt-1 text-xs text-muted-foreground font-normal">Урьдчилгаагүй, шимтгэлгүй 30-60 хоногт 1-4 хувааж төлөх боломжтой</span>
                      </div>
                    </div>
                  )}

                  {showOmniway && (
                    <div className="flex items-center gap-5 font-medium border border-border rounded-md px-4 py-3 shadow-xs">
                      <Image src={Omniway} alt="OmniWay" className="h-8 w-8 object-cover rounded" />
                      <div className="flex flex-col gap-0.5 w-full">
                        <div className="flex items-center justify-between">
                          <span className="text-base text-foreground/90 leading-5 tracking-[.0125em]">OmniWay</span>
                          <span className="text-xs text-muted-foreground font-medium italic whitespace-nowrap"><span className="hidden lg:inline xl:hidden">(100,000₮+)</span><span className="inline lg:hidden xl:inline">(Зөвхөн 100,000₮-с дээш)</span></span>
                        </div>
                        <span className="mt-1 text-xs text-muted-foreground font-normal">Хуваан төлөх боломжтой</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          })()}
          <div className="flex flex-col gap-2 mt-6">
            <div className="bg-muted hover:bg-muted-foreground/20 transition-colors rounded-md py-2 cursor-pointer">
              <div onClick={() => handleTermsClick('delivery')} className="flex items-center justify-center gap-2 font-medium">
                <Truck className="h-5 w-5 text-foreground/90" />
                <span className="text-sm text-foreground/90 leading-5 tracking-[.0125em]">Хүргэлтийн нөхцөл</span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-2">
              <div onClick={() => handleTermsClick('payment')} className="bg-muted hover:bg-muted-foreground/20 transition-colors rounded-md py-2 cursor-pointer">
                <span className="flex items-center justify-center font-medium text-sm text-foreground/90 leading-5 tracking-[.0125em]">Төлбөрийн нөхцөл</span>
              </div>
              <div onClick={() => handleTermsClick('service')} className="bg-muted hover:bg-muted-foreground/20 transition-colors rounded-md py-2 cursor-pointer">
                <span className="flex items-center justify-center font-medium text-sm text-foreground/90 leading-5 tracking-[.0125em]">Үйлчилгээний нөхцөл</span>
              </div>
            </div>
          </div>
        </div>
        {product?.description && (
          <div className="lg:col-span-8 lg:col-start-1 lg:row-start-2 lg:self-start">
            <div className="flex flex-col gap-2">
              <span className="text-foreground leading-6 font-medium">Бүтээгдэхүүний дэлгэрэнгүй</span>
              {/* Type scale, colour and block spacing all live in `.product-description`
                  (globals.css) so the vendor panel's editor can render the exact same way. */}
              <ProductDescription html={product.description} productName={product.name} vendor={vendor} className="product-description" />
            </div>
          </div>
        )}
        {(vendor.canReview && isCustomerAuthActive(vendor)) && (
          <div className="py-6 sm:py-8 block sm:hidden">
            <Review product={product} canReview={canReview} vendor={vendor} reviews={reviews} />
          </div>
        )}
      </div>
      {(vendor.canReview && isCustomerAuthActive(vendor)) && (
        <div className="py-8 sm:py-8 hidden sm:block">
          <Review product={product} canReview={canReview} vendor={vendor} reviews={reviews} />
        </div>
      )}
      {suggestedProducts.length > 0 && (
        <div className="mt-5">
          <div className="font-medium text-xl leading-7 tracking-[.0125em] text-foreground">
            Санал болгох бүтээгдэхүүн
          </div>
          <div className="mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-8 mt-5">
              {suggestedProducts.map((p: any) => (
                <ProductCard key={p.customId} product={p} vendor={vendor} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function FileRow({ file, dense }: { file: { name: string, size: number }, dense?: boolean }) {
  return (
    <div className={`flex items-center gap-2.5 rounded-md bg-muted/60 border border-border ${dense ? 'px-2.5 py-1.5 text-[11.5px]' : 'px-3 py-2 text-[12px]'}`}>
      <FileText className={`${dense ? 'h-3.5 w-3.5' : 'h-4 w-4'} shrink-0 text-muted-foreground`} strokeWidth={1.75} />
      <span className="font-medium truncate flex-1">{file.name}</span>
      <span className="tabular-nums text-muted-foreground shrink-0">{formatFileSize(file.size)}</span>
      <Download className={`${dense ? 'h-3.5 w-3.5' : 'h-4 w-4'} shrink-0 text-muted-foreground`} strokeWidth={1.75} />
    </div>
  )
}

function FulfillmentBanner({ product, variant }: { product: any, variant: any }) {
  const items: { key: 'digital' | 'limited' | 'order', icon: any, title: string, short: any, file?: { name: string, size: number } | null, color: string }[] = []

  if (product?.isDigital) {
    items.push({
      key: 'digital',
      icon: FileDown,
      title: 'Дижитал бүтээгдэхүүн',
      short: 'Худалдан авсны дараа татаж авна',
      file: variant?.digitalFile || null,
      color: '#2563eb',
    })
  }
  if (product?.isLimitedEdition) {
    items.push({
      key: 'limited',
      icon: Crown,
      title: 'Limited Edition',
      short: 'Ховор загварын бүтээгдэхүүн',
      color: '#ea580c',
    })
  }
  if (product?.isOrderBased) {
    items.push({
      key: 'order',
      icon: Hammer,
      title: 'Захиалгаар бэлтгэдэг',
      short: product?.orderBasedPeriod
        ? <><span className="tabular-nums font-medium text-foreground">{product.orderBasedPeriod}</span></>
        : 'Тодорхой хугацаанд бэлэн болно',
      color: '#16a34a',
    })
  }

  if (items.length === 0) return null

  return (
    <div className="mt-6">
      <div className="rounded-xl border border-border bg-background overflow-hidden">
        {items.map((it, i) => {
          const Icon = it.icon
          return (
            <div key={it.key} className={`p-3.5 ${i > 0 ? 'border-t border-border' : ''}`}>
              <div className="flex gap-3">
                <div className="w-0.75 rounded-full self-stretch shrink-0" style={{ background: it.color }} />
                <Icon className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.75} style={{ color: it.color }} />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold leading-tight">{it.title}</div>
                  <p className="mt-0.5 text-[12.5px] text-muted-foreground leading-snug">{it.short}</p>
                  {it.key === 'digital' && it.file && (
                    <div className="mt-2.5"><FileRow file={it.file} dense /></div>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const TONE: Record<string, { dot: string, label: string, badgeBg: string, badgeFg: string }> = {
  productDiscount: { dot: '#2563eb', label: 'Хямдрал', badgeBg: '#dbeafe', badgeFg: '#1d4ed8' },
  buyXgetY:        { dot: '#16a34a', label: 'Бэлэг',   badgeBg: '#dcfce7', badgeFg: '#15803d' },
  freeShipping:    { dot: '#0d9488', label: 'Хүргэлт', badgeBg: '#ccfbf1', badgeFg: '#0f766e' },
}

function DiscountLedger({ discounts, pathname }: { discounts: any[], pathname: string }) {
  if (!discounts || discounts.length === 0) return null
  const [open, setOpen] = useState(true)
  const maxPct = discounts
    .filter((d) => d.type === 'productDiscount' && d.valueType === 'percent')
    .reduce((m, d) => Math.max(m, d.valuePercent || 0), 0)
  const hasBx = discounts.some((d) => d.type === 'buyXgetY')
  const hasFs = discounts.some((d) => d.type === 'freeShipping')
  const summary = [
    maxPct > 0 && `-${maxPct}% хүртэл`,
    hasBx && 'Бэлэгтэй',
    hasFs && 'Үнэгүй хүргэлт',
  ].filter(Boolean).join(' · ')
  return (
    <div className="mt-4">
      <div className="rounded-xl border border-border bg-background overflow-hidden">
        <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors">
          <div className="h-7 w-7 grid place-items-center rounded-md shrink-0" style={{ background: 'linear-gradient(135deg,#dbeafe,#dcfce7)' }}>
            <Percent className="h-3.5 w-3.5 text-blue-700" strokeWidth={2} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-semibold leading-tight">{discounts.length} урамшуулал идэвхтэй</div>
            {summary && <div className="mt-0.5 text-[11.5px] text-muted-foreground leading-snug truncate">{summary}</div>}
          </div>
          <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        {open && (
          <div className="border-t border-border">
            {discounts.map((d, i) => (
              <div key={d.id || i} className={i < discounts.length - 1 ? 'border-b border-border' : ''}>
                <DiscountLedgerRow d={d} pathname={pathname} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

function DiscountLedgerRow({ d, pathname }: { d: any, pathname: string }) {
  const tone = TONE[d.type as keyof typeof TONE]
  if (!tone) return null

  if (d.type === 'productDiscount') {
    const badge = d.valueType === 'percent' && d.valuePercent
      ? `-${d.valuePercent}%`
      : d.valueType === 'amount' && d.valueAmount
        ? `-${formatPrice(d.valueAmount)}`
        : null
    return (
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{tone.label}</span>
            {badge && (
              <span className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded" style={{ background: tone.badgeBg, color: tone.badgeFg }}>{badge}</span>
            )}
          </div>
          <div className="mt-0.5 text-[13px] font-semibold leading-tight">{d.name}</div>
          <div className="mt-0.5 text-[12.5px] text-muted-foreground leading-snug">Энэхүү урамшуулал бараан дээр идэвхтэй байна.</div>
          {d.haveCampaignPage && d.campaignPageHandle && (
            <a href={navigateUrl(`/c/${d.campaignPageHandle}`, pathname)} className="inline-flex items-center gap-1 mt-1.5 text-[12px] font-medium text-blue-600 hover:underline">
              Урамшууллын хуудас үзэх <ArrowRight className="h-3 w-3" strokeWidth={2.25} />
            </a>
          )}
        </div>
      </div>
    )
  }

  if (d.type === 'buyXgetY') {
    const buyX = d.buyXMinQuantity || 1
    const getY = d.getYMaxQuantity || 1
    const reward = d.rewardProducts?.[0] || {}
    const free = reward.valueType === 'free' || !reward.valueType
    const pctVal = reward.valuePercent || d.valuePercent || 0
    const triggers = d.triggerProducts || []
    const isAll = d.buyXMatchMode === 'all' && triggers.length > 1
    const fallbackTitle = getBuyXGetYFallbackTitle(d)
    const tagText = getBuyXGetYTagText(d)
    return (
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{tone.label}</span>
            <span className="text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded" style={{ background: tone.badgeBg, color: tone.badgeFg }}>{tagText}</span>
          </div>
          <div className="mt-0.5 text-[13px] font-semibold leading-tight">{d.name || fallbackTitle}</div>
          <div className="mt-0.5 text-[12.5px] text-muted-foreground leading-snug">
            {isAll ? `Тус бүрээс багадаа ${buyX} ширхэг авбал ` : `${buyX} авбал `}
            {free ? `${getY} бэлэг үнэгүй.` : `${getY} бэлэг ${pctVal}% хямдралтай.`}
          </div>
          {isAll && triggers.length > 0 && (
            <div className="mt-2.5 grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(triggers.length, 3)}, 1fr)` }}>
              {triggers.map((tp: any) => {
                const hasDiscount = tp.priceSale && tp.priceSale > 0 && tp.priceSale < tp.price
                const eff = hasDiscount ? tp.priceSale : tp.price
                return (
                  <a key={tp.id} href={navigateUrl(`/product/${tp.customId || tp.id}`, pathname)} className="flex items-center gap-2 p-2 rounded-md border border-border hover:border-green-600 hover:bg-green-50/50 transition-colors">
                    {tp.image && <img src={formatImageURL(tp.image)} alt="" className="h-9 w-9 rounded-sm object-cover bg-muted shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-[11.5px] font-medium leading-tight truncate">{tp.name}</div>
                      {tp.price != null && (
                        <div className="mt-0.5 flex items-baseline gap-1.5">
                          <span className={`text-[11px] tabular-nums ${hasDiscount ? 'text-red-600 font-medium' : 'text-foreground'}`}>{formatPrice(eff)}</span>
                          {hasDiscount && <span className="text-[10px] tabular-nums line-through text-muted-foreground">{formatPrice(tp.price)}</span>}
                        </div>
                      )}
                    </div>
                  </a>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  if (d.type === 'freeShipping') {
    return (
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: tone.dot }} />
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{tone.label}</span>
          <div className="mt-0.5 text-[13px] font-semibold leading-tight">Үнэгүй хүргэлт</div>
          <div className="mt-0.5 text-[12.5px] text-muted-foreground leading-snug">
            <span className="font-medium text-foreground">{d.name}</span> — энэхүү бараан дээр үнэгүй хүргэлт идэвхтэй.
          </div>
          {d.isCampaign && d.campaignName && (
            <div className="mt-1 text-[11.5px] text-muted-foreground">{d.campaignName}</div>
          )}
        </div>
      </div>
    )
  }

  return null
}